import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PrimaryButton, SecondaryButton, IconButton } from "@/components/StandardButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Lock, Mail, Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2, Check, X } from "lucide-react";
import { signInSchema, signUpSchema } from "@/lib/validation/authSchemas";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

import { ForgotPasswordDialog } from "@/components/auth/ForgotPasswordDialog";
import { logger } from "@/utils/logger";

import { hapticSuccess, hapticError } from "@/utils/haptics";
import { isMobileDevice } from "@/utils/mobileOptimizations";
import Logo from "@/components/Logo";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const [isSignUp, setIsSignUp] = useState(searchParams.get('mode') === 'signup');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const [accountRecoveryPending, setAccountRecoveryPending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [otpCode, setOtpCode] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Password validation requirements
  const requirements = [
    { label: "At least 8 characters", test: (pwd: string) => pwd.length >= 8 },
    { label: "Contains uppercase letter", test: (pwd: string) => /[A-Z]/.test(pwd) },
    { label: "Contains lowercase letter", test: (pwd: string) => /[a-z]/.test(pwd) },
    { label: "Contains a number", test: (pwd: string) => /\d/.test(pwd) },
    { label: "Contains a symbol", test: (pwd: string) => /[^a-zA-Z0-9]/.test(pwd) },
  ];

  const getRequirementStatus = (requirement: { test: (pwd: string) => boolean }) => {
    if (!password) return null;
    return requirement.test(password);
  };

  // Redirect already authenticated users to dashboard
  useEffect(() => {
    const redirectIfAuthenticated = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          navigate("/dashboard");
        }
      } finally {
        setInitialLoading(false);
      }
    };

    redirectIfAuthenticated();
  }, [navigate]);

  // Validate email in real-time
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsEmailValid(emailRegex.test(email.trim()));
  }, [email]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Blur active input to close mobile keyboard
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Auto-scroll to top on mobile to show any errors
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Validate form
    const schema = isSignUp ? signUpSchema : signInSchema;
    const validation = schema.safeParse({ email, password });

    if (!validation.success) {
      const fieldErrors: any = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0]] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const { error, data } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/auth`,
          },
        });

        if (error) {
          if (error.message.includes("already registered")) {
            // Trigger password reset for existing account instead of showing error
            await supabase.auth.resetPasswordForEmail(email.trim(), {
              redirectTo: `${window.location.origin}/reset-password`,
            });
            setAccountRecoveryPending(true);
            hapticSuccess();
            toast({
              title: "Account found!",
              description: "We've sent you a password reset link.",
            });
            setLoading(false);
            return;
          }
          throw error;
        }

        // Check if email confirmation is required
        if (data.user && !data.user.email_confirmed_at) {
          // Email verification is required
          setEmailVerificationPending(true);
          hapticSuccess();
          toast({
            title: "Verification email sent!",
            description: "Please check your inbox and spam folder.",
          });
          return;
        }

        // If email is already confirmed (auto-confirm enabled), proceed with session
        let sessionInitialized = false;
        let retries = 0;
        const maxRetries = 5;
        
        while (!sessionInitialized && retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          if (sessionError) {
            logger.error("Session error during initialization", sessionError);
            retries++;
            continue;
          }
          
          if (session) {
            sessionInitialized = true;
            break;
          }
          
          retries++;
        }
        
        if (!sessionInitialized) {
          logger.error("Failed to initialize session after signup");
          toast({
            title: "Session Error",
            description: "Please try logging in again.",
            variant: "destructive"
          });
          return;
        }
        
        hapticSuccess();
        toast({
          title: "Account created!",
          description: "Welcome to SendFeedback!",
        });
        navigate("/dashboard");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        
        // If remember me is enabled, update session to persist longer
        if (!error && rememberMe) {
          await supabase.auth.refreshSession();
        }

        if (error) {
          // Check for actual unverified email error
          if (error.message.includes("Email not confirmed")) {
            setEmailVerificationPending(true);
            toast({
              title: "Email not verified",
              description: "Please verify your email before signing in.",
            });
            setLoading(false);
            return;
          }
          
          // Show generic error for invalid credentials
          if (error.message.includes("Invalid login credentials")) {
            throw new Error("Invalid email or password. Please check your credentials and try again.");
          }
          
          throw error;
        }

        hapticSuccess();
        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });
        navigate(redirectPath || "/dashboard");
      }
    } catch (error: any) {
      hapticError();
      toast({
        title: "Authentication failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };


  const toggleMode = () => {
    const newMode = !isSignUp;
    setIsSignUp(newMode);
    setErrors({});
    setPassword("");
    setRememberMe(false);
    setEmailVerificationPending(false);
    setAccountRecoveryPending(false);
    
    // Update URL parameter
    navigate(`/auth?mode=${newMode ? 'signup' : 'signin'}`, { replace: true });
  };

  const handleResendPasswordReset = async () => {
    if (resendCooldown > 0) return;

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: "Email resent!",
        description: "Check your inbox for the password reset link.",
      });

      // Start 60-second cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      toast({
        title: "Failed to resend email",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email.trim(),
      });

      if (error) throw error;

      toast({
        title: "Email resent!",
        description: "Check your inbox for the verification code.",
      });

      // Start 60-second cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      toast({
        title: "Failed to resend email",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter the 6-digit code from your email.",
        variant: "destructive",
      });
      return;
    }

    setVerifyingOtp(true);

    try {
      const { error, data } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: otpCode,
        type: 'signup',
      });

      if (error) throw error;

      if (data.session) {
        hapticSuccess();
        toast({
          title: "Email verified!",
          description: "Welcome to SendFeedback!",
        });
        navigate("/dashboard");
      }
    } catch (error: any) {
      hapticError();
      toast({
        title: "Verification failed",
        description: error.message === "Token has expired or is invalid" 
          ? "The code has expired or is invalid. Please request a new one."
          : error.message,
        variant: "destructive",
      });
      setOtpCode("");
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col lg:flex-row">
      {/* Left side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12">
        <div className="w-full max-w-lg">
          {/* Header with Back button and Logo */}
          <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-4 mb-6 sm:mb-8">
            {/* Logo - centered on mobile, right on desktop */}
            <div className="sm:order-2">
              <Logo size="sm" />
            </div>
            {/* Back button - centered on mobile, left on desktop */}
            <div className="sm:order-1">
              <SecondaryButton
                className="text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-4"
                onClick={() => navigate("/")}
              >
                <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                Back
              </SecondaryButton>
            </div>
          </div>
          <Card className="w-full border-0 shadow-none lg:border lg:shadow-sm">
          <CardHeader className="space-y-3 sm:space-y-4 text-center px-4 sm:px-6 pt-4 sm:pt-6">
            <div className="space-y-2">
              <CardTitle className="text-xl sm:text-2xl">
                {accountRecoveryPending 
                  ? "Account found!" 
                  : (emailVerificationPending 
                    ? "Check your email" 
: (isSignUp ? <><span className="bg-green-100 text-black px-1.5 py-0.5 rounded">Create your account</span></> : <><span className="bg-success/20 px-1.5 py-0.5 rounded">Welcome back</span></>))}
              </CardTitle>
              <CardDescription className="text-sm sm:text-base">
                {accountRecoveryPending
                  ? `We found an existing account with ${email}`
                  : (emailVerificationPending 
                    ? `We've sent a verification link to ${email}`
                    : (isSignUp
                      ? <><span className="block sm:inline">Start collecting 100% anonymous</span><span className="block sm:inline"> feedback in <span className="bg-black text-white px-1.5 py-0.5 rounded">30 seconds</span></span></>
                      : "Sign in to continue to your dashboard"))}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
            {accountRecoveryPending ? (
              <div className="space-y-6 text-center py-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <Mail className="h-16 w-16 text-primary animate-pulse" />
                    <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    We've sent a password reset link to your email. Click the link to set a new password and sign in.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Didn't receive it? Check your spam folder or request a new one.
                  </p>
                </div>

                <div className="space-y-3">
                  <PrimaryButton
                    onClick={handleResendPasswordReset}
                    disabled={resendCooldown > 0}
                    className="w-full"
                  >
                    {resendCooldown > 0 ? (
                      <>Resend in {resendCooldown}s</>
                    ) : (
                      <>Resend password reset email</>
                    )}
                  </PrimaryButton>

                  <SecondaryButton
                    onClick={() => {
                      setAccountRecoveryPending(false);
                      setIsSignUp(false);
                      navigate('/auth?mode=signin', { replace: true });
                    }}
                    className="w-full"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </SecondaryButton>
                </div>
              </div>
            ) : emailVerificationPending ? (
              <div className="space-y-6 text-center py-4">
                <div className="flex justify-center">
                  <div className="relative">
                    <Mail className="h-16 w-16 text-primary animate-pulse" />
                    <div className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code we sent to <span className="font-medium text-foreground">{email}</span>
                  </p>
                </div>

                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpCode}
                    onChange={(value) => setOtpCode(value)}
                    disabled={verifyingOtp}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <div className="space-y-3">
                  <PrimaryButton
                    onClick={handleVerifyOtp}
                    disabled={otpCode.length !== 6 || verifyingOtp}
                    className="w-full"
                  >
                    {verifyingOtp ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>Verify Email</>
                    )}
                  </PrimaryButton>

                  <SecondaryButton
                    onClick={handleResendVerification}
                    disabled={resendCooldown > 0}
                    className="w-full"
                  >
                    {resendCooldown > 0 ? (
                      <>Resend in {resendCooldown}s</>
                    ) : (
                      <>Resend code</>
                    )}
                  </SecondaryButton>

                  <p className="text-xs text-muted-foreground">
                    Didn't receive it? Check your spam folder.
                  </p>

                  <SecondaryButton
                    onClick={() => {
                      setEmailVerificationPending(false);
                      setOtpCode("");
                      setIsSignUp(false);
                      navigate('/auth?mode=signin', { replace: true });
                    }}
                    variant="ghost"
                    className="w-full"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </SecondaryButton>
                </div>
              </div>
            ) : (
            <>
            <form onSubmit={handleAuth} className="space-y-4 sm:space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm sm:text-base">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9 pr-10 min-h-[52px] sm:min-h-[48px] text-base"
                    disabled={loading}
                    required
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {isEmailValid && email.length > 0 && (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500 animate-in fade-in zoom-in duration-200" />
                  )}
                </div>
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive">
                    {errors.email}
                  </p>
                )}
              </div>


              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm sm:text-base">Password</Label>
                  {!isSignUp && (
                    <button
                      type="button"
                      onClick={() => setForgotPasswordOpen(true)}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 pr-12 min-h-[52px] sm:min-h-[48px] text-base"
                    disabled={loading}
                    required
                    minLength={6}
                    autoComplete={isSignUp ? "new-password" : "current-password"}
                    aria-invalid={!!errors.password}
                    aria-describedby={errors.password ? "password-error" : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-1 top-1 text-muted-foreground hover:text-foreground transition-colors w-12 h-12 sm:w-10 sm:h-10 flex items-center justify-center rounded-md"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p id="password-error" className="text-sm text-destructive">
                    {errors.password}
                  </p>
                )}
                {isSignUp && password && (
                  <PasswordStrengthIndicator password={password} />
                )}
              </div>

              {!isSignUp && (
                <div className="flex items-center space-x-2 py-1">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked === true)}
                    disabled={loading}
                  />
                  <Label 
                    htmlFor="rememberMe" 
                    className="text-sm font-normal cursor-pointer select-none"
                  >
                    Remember me for 30 days
                  </Label>
                </div>
              )}

              <PrimaryButton type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isSignUp ? "Creating account..." : "Signing in..."}
                  </>
                ) : (
                  isSignUp ? "Create Account" : "Sign In"
                )}
              </PrimaryButton>

              {isSignUp && (
                <>
                  {/* Trust Signals */}
                  <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Check className="h-3 w-3 text-primary" />
                      <span className="bg-success/20 px-1.5 py-0.5 rounded">No card needed</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="h-3 w-3 text-primary" />
                      <span className="bg-success/20 px-1.5 py-0.5 rounded">30-second setup</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="h-3 w-3 text-primary" />
                      <span className="bg-success/20 px-1.5 py-0.5 rounded">Cancel anytime</span>
                    </span>
                  </div>

                  <p className="text-xs text-center text-muted-foreground">
                    By signing up, you agree to our{" "}
                    <Link to="/terms-of-service" className="text-primary hover:underline">
                      Terms of Service
                    </Link>
                    {" "}and{" "}
                    <Link to="/privacy-policy" className="text-primary hover:underline">
                      Privacy Policy
                    </Link>
                  </p>
                </>
              )}
            </form>


            <div className="mt-5 sm:mt-6 text-center">
              <button
                type="button"
                onClick={toggleMode}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                disabled={loading}
              >
                {isSignUp
                  ? "Already have an account? Sign in"
                  : "Don't have an account? Sign up"}
              </button>
            </div>
            </>
            )}
          </CardContent>
        </Card>
        </div>
      </div>


      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog 
        open={forgotPasswordOpen} 
        onOpenChange={setForgotPasswordOpen}
        currentMode={isSignUp ? 'signup' : 'signin'}
      />
    </div>
  );
};

export default Auth;
